import { useEffect } from 'react';
import { BRAND } from '@maintenance-tracker/shared';

export interface SeoProps {
  title: string;
  description: string;
  canonicalPath?: string;
  ogType?: 'website' | 'article';
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  noindex?: boolean;
}

const SITE = `https://${BRAND.domain}`;

function upsertMeta(name: string, content: string, attr: 'name' | 'property' = 'name') {
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.content = content;
}

function upsertLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

function removeJsonLdScripts() {
  document.querySelectorAll('script[data-seo-jsonld]').forEach((n) => n.remove());
}

export function SeoHead({
  title,
  description,
  canonicalPath,
  ogType = 'website',
  jsonLd,
  noindex,
}: SeoProps) {
  useEffect(() => {
    const fullTitle = title.includes(BRAND.name) ? title : `${title} | ${BRAND.name}`;
    document.title = fullTitle;

    upsertMeta('description', description);
    upsertMeta('og:title', fullTitle, 'property');
    upsertMeta('og:description', description, 'property');
    upsertMeta('og:type', ogType, 'property');
    upsertMeta('twitter:card', 'summary_large_image');
    upsertMeta('twitter:title', fullTitle);
    upsertMeta('twitter:description', description);

    if (noindex) {
      upsertMeta('robots', 'noindex, nofollow');
    } else {
      upsertMeta('robots', 'index, follow');
    }

    if (canonicalPath) {
      const url = `${SITE}${canonicalPath}`;
      upsertLink('canonical', url);
      upsertMeta('og:url', url, 'property');
    }

    removeJsonLdScripts();
    if (jsonLd) {
      const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      for (const block of blocks) {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo-jsonld', '1');
        script.textContent = JSON.stringify(block);
        document.head.appendChild(script);
      }
    }

    return () => {
      removeJsonLdScripts();
    };
  }, [title, description, canonicalPath, ogType, jsonLd, noindex]);

  return null;
}

export function organizationJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: BRAND.name,
    url: SITE,
    description: BRAND.description,
    email: BRAND.helloEmail,
  };
}

export function breadcrumbJsonLd(items: { name: string; path: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: `${SITE}${item.path}`,
    })),
  };
}

export function faqJsonLd(faqs: { q: string; a: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}

export function articleJsonLd(opts: {
  title: string;
  description: string;
  path: string;
  published: string;
  updated: string;
  author?: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: opts.title,
    description: opts.description,
    datePublished: opts.published,
    dateModified: opts.updated,
    author: {
      '@type': 'Organization',
      name: opts.author ?? 'Renewal Drive Editorial Team',
    },
    publisher: organizationJsonLd(),
    mainEntityOfPage: `${SITE}${opts.path}`,
  };
}
